import { ReactElement, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  Active,
  Announcements,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  MeasuringConfiguration,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  Card as ItemCard,
  Info,
  Plus,
  SaladIcon,
  SoftButton,
  Tooltip,
  Typography,
} from '@weareopenr/component-library';
import { MenuTrayKind, useMenuTray } from 'src/contexts/menuTray';
import { SectionTrayContent } from 'src/components/SectionTray';
import { ManageAvailabilityTrayContent } from 'src/components/ManageAvailabilityTray';
import Divider from '../Divider';
import MenuSection, { ISectionTrayTarget } from '../MenuSection';
import { AddProductsToMenuTrayContent } from './AddProductsToMenuTray';
import MenuItemCardContainer from './MenuItemCardContainer';
import MenuItemSectionContainer from './MenuItemSectionContainer';
import { createCategoriesHierarchy, ICategoryWithChildren, isCategoryAssignedToMealtime } from './utilities';
import { appendedDisplayOrder, getDropResult } from 'src/helpers/getDropResult';
import type { IDropResult } from 'src/helpers/getDropResult';
import { moveItemBetweenSections } from 'src/helpers/moveItemBetweenSections';
import { reassignDisplayOrders } from 'src/helpers/reassignDisplayOrders';
import { reorderItems } from 'src/helpers/reorderItems';
import { useMoveItemToSection } from 'src/hooks/useMoveItemToSection';
import { useReorderMenuItem } from 'src/hooks/useReorderMenuItem';
import { useSerialQueue } from 'src/hooks/useSerialQueue';
import { IMenu, IMenuItem, ISection } from 'src/types/menu.types';
import styles from './MenuSectionItems.module.scss';
import { CreateGlobalItemWizard } from '../CreateGlobalItemWizard/CreateGlobalItemWizard';
import { toMap } from 'src/helpers';
import { getNextOpenId } from 'src/helpers/getNextOpenId';

// closestCenter always resolves the nearest droppable however far the pointer is, so releasing
// outside a section still produced a valid target and reordered the card. pointerWithin requires
// containment, but returns nothing for a keyboard drag, which has no pointer - so keyboard drags
// keep the rect-based strategy.
export const collisionDetection: CollisionDetection = (args) =>
  args.pointerCoordinates ? pointerWithin(args) : closestCenter(args);

const measuring: MeasuringConfiguration = { droppable: { frequency: 100 } };

type IReorderResult = Extract<IDropResult, { kind: 'reorder' }>;

interface IActiveDrag {
  item: IMenuItem;
  originSectionId: string;
}

const findSectionHoldingItem = (sections: ISection[], itemId: string): ISection | undefined =>
  sections.find(({ items }) => items.some((item) => item.itemId === itemId));

const applyReorder = (sections: ISection[], { sectionId, fromIndex, toIndex }: IReorderResult): ISection[] =>
  sections.map((section) =>
    section.sectionId === sectionId
      ? { ...section, items: reassignDisplayOrders(reorderItems(section.items, fromIndex, toIndex)) }
      : section,
  );

const MenuSectionItems = ({ menu }: { menu: IMenu }): ReactElement => {
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [isGlobalItemWizardOpen, setIsGlobalItemWizardOpen] = useState<boolean>(false);
  const [pendingAddition, setPendingAddition] = useState<{ sectionId: string | null; count: number }>({
    sectionId: null,
    count: 0,
  });
  const [scrollToSectionId, setScrollToSectionId] = useState<string | null>(null);
  const { openTray, closeTray, isTrayOpen, setTrayDirty } = useMenuTray();
  const { menuId, uncategorised, modifiers, mealtimes } = menu;

  const [activeDrag, setActiveDrag] = useState<IActiveDrag | null>(null);
  const [dragSections, setDragSections] = useState<ISection[] | null>(null);
  const arrangementAtDragStart = useRef<ISection[] | null>(null);
  const gestureId = useRef(0);
  const sections = dragSections ?? menu.sections;

  const categoriesHierachy = useMemo(() => createCategoriesHierarchy(sections), [sections]);
  const sectionsMap = useMemo(() => toMap(sections, 'sectionId'), [sections]);

  // The meta count must hold still until a drop is committed, so it is read from the cached menu
  // rather than the arrangement a hover previews into.
  const committedItemCounts = useMemo(
    () => new Map(menu.sections.map(({ sectionId, items }) => [sectionId, items.length])),
    [menu.sections],
  );

  const previewedItemId = useMemo(() => {
    if (!activeDrag) return null;
    const holding = findSectionHoldingItem(sections, activeDrag.item.itemId);

    return holding && holding.sectionId !== activeDrag.originSectionId ? activeDrag.item.itemId : null;
  }, [activeDrag, sections]);

  // Both endpoints write absolute display orders, so a burst of drops must reach the server in
  // gesture order; one queue shared by the two hooks is what keeps them from overtaking each other.
  const enqueueDisplayOrderWrite = useSerialQueue();
  const reorder = useReorderMenuItem(menuId, enqueueDisplayOrderWrite);
  const moveToSection = useMoveItemToSection(menuId, enqueueDisplayOrderWrite);
  const isDragSuspended = moveToSection.areDisplayOrdersUnsettled;

  const sensors = useSensors(
    // 8px so a click still follows the card's link rather than starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const findItemName = (id: string | number): string =>
    sections.flatMap(({ items }) => items).find(({ itemId }) => itemId === id)?.name ?? 'the item';

  const findSectionName = (id: string | number): string | undefined => sectionsMap.get(String(id))?.name;

  const movedOver = (id: string | number): string => {
    const sectionName = findSectionName(id);

    return sectionName ? `over the ${sectionName} section` : `over ${findItemName(id)}`;
  };

  const droppedOn = (id: string | number): string => {
    const sectionName = findSectionName(id);

    return sectionName ? `into the ${sectionName} section` : `over ${findItemName(id)}`;
  };

  const resolveOriginSectionId = (active: Active): string | undefined =>
    arrangementAtDragStart.current
      ? findSectionHoldingItem(arrangementAtDragStart.current, String(active.id))?.sectionId
      : (active.data.current?.sectionId as string | undefined);

  const endGesture = (): void => {
    setActiveDrag(null);
    arrangementAtDragStart.current = null;
  };

  const handleDragStart = ({ active }: DragStartEvent): void => {
    gestureId.current += 1;
    arrangementAtDragStart.current = sections;
    const origin = findSectionHoldingItem(sections, String(active.id));
    const item = origin?.items.find(({ itemId }) => itemId === active.id);
    setActiveDrag(origin && item ? { item, originSectionId: origin.sectionId } : null);
  };

  const handleDragOver = ({ active, over }: DragOverEvent): void => {
    const itemId = String(active.id);
    const toSectionId = over?.data.current?.sectionId as string | undefined;

    if (!toSectionId) {
      setDragSections(arrangementAtDragStart.current);
      return;
    }

    setDragSections((previous) => {
      const current = previous ?? menu.sections;
      const holding = findSectionHoldingItem(current, itemId);
      const destination = current.find((section) => section.sectionId === toSectionId);
      if (!holding || !destination || holding.sectionId === destination.sectionId) return previous;

      return moveItemBetweenSections(
        current,
        itemId,
        holding.sectionId,
        destination.sectionId,
        appendedDisplayOrder(destination),
      );
    });
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const arrangement = arrangementAtDragStart.current ?? sections;
    const result = isDragSuspended ? null : getDropResult(event, arrangement, resolveOriginSectionId(event.active));
    endGesture();

    if (!result) {
      setDragSections(null);
      return;
    }

    const gesture = gestureId.current;
    const settle = {
      onSettled: () => {
        if (gestureId.current === gesture) setDragSections(null);
      },
    };

    if (result.kind === 'reorder') {
      setDragSections(applyReorder(arrangement, result));
      reorder.mutate(result, settle);
      return;
    }

    moveToSection.mutate(result, settle);
  };

  const handleDragCancel = (): void => {
    endGesture();
    setDragSections(null);
  };

  // dnd-kit's defaults read the raw item id (a UUID in production) to a screen reader,
  // eg "Picked up draggable item de3ccc32-...". Naming the item instead keeps the
  // announcements meaningful.
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${findItemName(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${findItemName(active.id)} was moved ${movedOver(over.id)}.`
        : `${findItemName(active.id)} is no longer over a menu section.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `${findItemName(active.id)} was dropped ${droppedOn(over.id)}.`
        : `${findItemName(active.id)} was dropped.`,
    onDragCancel: ({ active }) =>
      `Dragging was cancelled. ${findItemName(active.id)} was returned to its original position.`,
  };

  const openSectionTray = (kind: MenuTrayKind, content: (openId: number) => ReactElement, title: string): void => {
    openTray(kind, content(getNextOpenId()), { title });
  };

  const handleSectionModal = (options: ISectionTrayTarget = {}): void => {
    const { parentSectionId: targetParentSectionId = null, section } = options;
    const effectiveParentSectionId = section ? section.parentSectionId : targetParentSectionId;

    openSectionTray(
      'edit-section',
      (openId) => (
        <SectionTrayContent
          openId={openId}
          menuId={menuId}
          parentSectionId={effectiveParentSectionId}
          section={section}
          onClose={closeTray}
          onDirtyChange={setTrayDirty}
          onSaved={(savedSectionId, isNew) => {
            if (isNew) setScrollToSectionId(savedSectionId);
          }}
        />
      ),
      `${section ? 'Edit' : 'Create'} menu ${effectiveParentSectionId ? 'sub-section' : 'section'}`,
    );
  };

  const handleManageAvailabilityModal = (section: ICategoryWithChildren): void => {
    openSectionTray(
      'section-availability',
      (openId) => (
        <ManageAvailabilityTrayContent
          openId={openId}
          menuId={menuId}
          section={section}
          sections={sections}
          onClose={closeTray}
          onDirtyChange={setTrayDirty}
        />
      ),
      'Menu Section Availability',
    );
  };

  const showSelectProductsModal = (secId?: string): void => {
    const targetSectionId = secId && secId.length > 0 ? secId : null;
    setSectionId(targetSectionId);
    const sectionName = targetSectionId ? sectionsMap.get(targetSectionId)?.name : undefined;

    openSectionTray(
      'add-products',
      (openId) => (
        <AddProductsToMenuTrayContent
          openId={openId}
          menuId={menuId}
          menuName={menu.name}
          sectionId={targetSectionId}
          menuRanges={menu.ranges}
          onCreateNewProduct={() => setIsGlobalItemWizardOpen(true)}
          onClose={closeTray}
          onDirtyChange={setTrayDirty}
          onSavingChange={(count) => setPendingAddition({ sectionId: count > 0 ? targetSectionId : null, count })}
        />
      ),
      sectionName ? `Add products to ${sectionName}` : 'Add products',
    );
  };

  const pendingCountFor = (targetSectionId: string | null) =>
    pendingAddition.sectionId === targetSectionId ? pendingAddition.count : 0;

  return (
    <>
      {menu && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          measuring={measuring}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          accessibility={{ announcements }}
        >
          <CreateGlobalItemWizard
            open={isGlobalItemWizardOpen}
            closeModal={() => setIsGlobalItemWizardOpen(false)}
            menuId={menuId}
            sectionId={sectionId}
          />
          <div className={styles['add-new-category']}>
            <SoftButton
              colour="blueberry"
              icon={Plus}
              size="full-width"
              disabled={isTrayOpen}
              onClick={() => handleSectionModal({ parentSectionId: null })}
            >
              Add menu section
            </SoftButton>
          </div>
          {categoriesHierachy.map((category: ICategoryWithChildren, index: number) => (
            <MenuItemSectionContainer
              key={category.sectionId}
              category={category}
              menuId={menuId}
              onClickNewItem={showSelectProductsModal}
              handleSectionModal={handleSectionModal}
              handleManageAvailabilityModal={handleManageAvailabilityModal}
              displayOrder={{
                up: categoriesHierachy[index - 1]?.displayOrder,
                down: categoriesHierachy[index + 1]?.displayOrder,
              }}
              isAssignedToMealtime={
                !mealtimes.length ? undefined : isCategoryAssignedToMealtime(category.sectionId, mealtimes)
              }
              tooltipPosition={index === 0 ? 'bottom' : 'top'}
              sections={sections}
              isAddProductsHidden={isTrayOpen}
              pendingAddition={pendingAddition}
              scrollToSectionId={scrollToSectionId}
              onScrolledIntoView={() => setScrollToSectionId(null)}
              previewedItemId={previewedItemId}
              isDragSuspended={isDragSuspended}
              committedItemCounts={committedItemCounts}
            />
          ))}
          {(uncategorised.length > 0 || sections.length === 0) && (
            <MenuSection
              variant="unassigned"
              menuId={menuId}
              section={{
                sectionId: '',
                name: 'Unassigned products',
                description:
                  'These products will not be published as part of your menu unless assigned to a menu section',
                displayOrder: 0,
                items: uncategorised,
                parentSectionId: null,
                children: [],
              }}
              onClickNewItem={showSelectProductsModal}
              isAssignedToMealtime={undefined}
              tooltipPosition="top"
              sections={sections}
              isAddProductsHidden={isTrayOpen}
              pendingAddition={pendingAddition}
            />
          )}
          {modifiers.length > 0 && (
            <>
              <Divider title="Modifiers">
                <Typography size="medium" weight="bold">
                  Modifiers
                </Typography>
                <div className={styles['info-tooltip-logo-wrapper']}>
                  <Tooltip text="Modifiers are saved and editable but won’t be published in the menu as separate products.">
                    <Info />
                  </Tooltip>
                </div>
              </Divider>
              {modifiers?.length > 0 && (
                <MenuItemCardContainer
                  items={modifiers}
                  menuId={menuId}
                  newItemButtonTitle="New modifier"
                  handleNewItemClick={() => showSelectProductsModal()}
                  isNewItemHidden={isTrayOpen}
                  pendingCount={pendingCountFor(null)}
                />
              )}
            </>
          )}

          <DragOverlay>
            {activeDrag && (
              <ItemCard
                text={activeDrag.item.name}
                subtext={activeDrag.item.consumerName ?? undefined}
                imageSrc={activeDrag.item.image ?? undefined}
                icon={SaladIcon}
                variant="quaternary"
              />
            )}
          </DragOverlay>
        </DndContext>
      )}
    </>
  );
};

export default MenuSectionItems;
